import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  academic_title: "", bio: "",
  codice_fiscale: "", date_of_birth: "", gender: "",
  address: "", city: "", country: "Italia",
  coordinated_area_id: "", subjects: "", notes: "",
  status: "attivo",
};
const TRACKS = [["residenziale", "Residenziale"], ["modulare", "Modulare"]];

export default function TeacherFormDialog({ open, onOpenChange, mode = "create", initial, onSubmit, submitting, defaultTrack = "residenziale" }) {
  const [form, setForm] = useState(EMPTY);
  const { data: areas = [] } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => (await api.get("/areas")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...EMPTY,
          ...initial,
          subjects: Array.isArray(initial.subjects) ? initial.subjects.join(", ") : (initial.subjects || ""),
          coordinated_area_id: initial.coordinated_area_id || "",
          tracks: initial.tracks?.length ? initial.tracks : ["residenziale"],
        });
      } else {
        setForm({ ...EMPTY, tracks: [defaultTrack] });
      }
    }
  }, [open, initial, defaultTrack]);

  const toggleTrack = (t) => setForm((p) => {
    const cur = p.tracks || [];
    const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
    return next.length ? { ...p, tracks: next } : p;
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    payload.subjects = form.subjects
      ? form.subjects.split(",").map((x) => x.trim()).filter(Boolean)
      : [];
    // clean empties → null
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    payload.status = form.status || "attivo";
    onSubmit(payload);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90dvh] overflow-y-auto overscroll-contain" data-testid="teacher-form-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuovo docente" : "Modifica docente"}</DialogTitle>
          <DialogDescription>Anagrafica completa, area di coordinamento e materie insegnate.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <Label className="text-xs">Nome</Label>
            <Input data-testid="teacher-first-name" value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} required />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Cognome</Label>
            <Input data-testid="teacher-last-name" value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} required />
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Titolo accademico</Label>
            <Input data-testid="teacher-title" placeholder="Prof. / Dr. / Past." value={form.academic_title || ""} onChange={(e) => set("academic_title", e.target.value)} />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Data di nascita</Label>
            <Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} />
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Email</Label>
            <Input type="email" data-testid="teacher-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Telefono</Label>
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Codice Fiscale</Label>
            <Input className="uppercase mono" value={form.codice_fiscale || ""} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Genere</Label>
            <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="M">M</SelectItem>
                <SelectItem value="F">F</SelectItem>
                <SelectItem value="altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Indirizzo</Label>
            <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Città</Label>
            <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Paese</Label>
            <Input value={form.country || ""} onChange={(e) => set("country", e.target.value)} />
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Area di coordinamento</Label>
            <Select value={form.coordinated_area_id || "__none"} onValueChange={(v) => set("coordinated_area_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="teacher-area"><SelectValue placeholder="Nessuna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Nessuna</SelectItem>
                {areas.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`teacher-area-opt-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Stato</Label>
            <Select value={form.status || "attivo"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="attivo">Attivo</SelectItem>
                <SelectItem value="sospeso">Sospeso</SelectItem>
                <SelectItem value="cessato">Cessato</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Insegna nella scuola</Label>
            <div className="flex gap-2 mt-1">
              {TRACKS.map(([v, l]) => {
                const on = (form.tracks || []).includes(v);
                return (
                  <button type="button" key={v} data-testid={`teacher-track-${v}`} onClick={() => toggleTrack(v)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-600 border-stone-300 hover:border-[#1C352D]"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-stone-500 mt-1">Seleziona entrambe se il docente insegna in tutti e due i percorsi.</p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Materie insegnate (separa con virgola)</Label>
            <Input data-testid="teacher-subjects" value={form.subjects || ""} onChange={(e) => set("subjects", e.target.value)} placeholder="es. Sistematica, Ebraico, Storia della Chiesa" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Bio breve</Label>
            <Textarea rows={3} value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Note interne</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
          </div>

          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting} data-testid="teacher-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : mode === "create" ? "Crea docente" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
